/* Section carousel jobs run after deferred vendor, using its captured jQuery/Slick.
 * No global $ lookup, second library download, viewport-delay or scroll gate. */
(function () {
  'use strict';
  if (window.FHSectionCarousels) return;
  const script = document.currentScript;
  const cssUrl = script && script.dataset.fhCarouselCss;
  const $ = window.theme && window.theme.jQuery;
  const slick = $ && $.fn && $.fn.slick;
  const jobs = new Map();
  let stylesPromise;

  function styles() {
    if (stylesPromise) return stylesPromise;
    stylesPromise = new Promise(function (resolve) {
      let link = document.querySelector('link[data-fh-carousel-styles]');
      if (!link && !cssUrl) { resolve(false); return; }
      if (link && link.sheet) { resolve(true); return; }
      const created = !link;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        link.setAttribute('data-fh-carousel-styles', '');
      }
      let timer;
      function finish(ok) {
        clearTimeout(timer);
        link.removeEventListener('load', loaded);
        link.removeEventListener('error', failed);
        resolve(ok);
      }
      function loaded() { finish(true); }
      function failed() { finish(false); }
      link.addEventListener('load', loaded);
      link.addEventListener('error', failed);
      // A failed/already-failed stylesheet must not leave jobs waiting forever.
      timer = setTimeout(failed, 8000);
      if (created) document.head.appendChild(link);
    });
    return stylesPromise;
  }

  function teardown(job) {
    for (const entry of job.entries) {
      if (!entry.owned) continue;
      cancelResponsiveTimer(entry.instance);
      // Another owner must never be unslicked through our stale registration.
      if (entry.element.slick !== entry.instance) {
        entry.owned = false;
        entry.instance = null;
        continue;
      }
      try { slick.call($(entry.element), 'unslick'); }
      catch (_) { restore(entry); }
      finally { cancelResponsiveTimer(entry.instance); }
      entry.owned = false;
      entry.instance = null;
      entry.element.dataset.fhCarouselState = 'static';
    }
  }

  function cancelResponsiveTimer(instance) {
    // Bundled Slick's destroy() removes resize handlers but leaves windowDelay.
    // That queued callback calls checkResponsive() BEFORE checking unslicked,
    // so it can refresh/rebuild a destroyed mobile carousel at desktop width.
    // Cancel only the instance this lifecycle created; never patch core Slick.
    if (!instance) return;
    clearTimeout(instance.windowDelay);
    instance.windowDelay = null;
  }

  function restore(entry) {
    // Keep original nodes (and their listeners), never cloned fallback content.
    entry.element.replaceChildren.apply(entry.element, entry.children);
    if (entry.className === null) entry.element.removeAttribute('class');
    else entry.element.setAttribute('class', entry.className);
    if (entry.style === null) entry.element.removeAttribute('style');
    else entry.element.setAttribute('style', entry.style);
    for (const child of entry.childAttributes) {
      for (const name of ['class', 'style', 'tabindex', 'aria-hidden', 'role', 'id']) {
        if (child.values[name] === null) child.element.removeAttribute(name);
        else child.element.setAttribute(name, child.values[name]);
      }
    }
  }

  function update(job) {
    if (job.disposed || !job.root.isConnected) return;
    if (job.media && !job.media.matches) { teardown(job); return; }
    for (const entry of job.entries) {
      const element = entry.element;
      if (entry.failed || entry.owned || !element.isConnected || !element.children.length) continue;
      // Never adopt/destroy a carousel owned by the theme or another extension.
      if (element.classList.contains('slick-initialized')) continue;
      if (!job.ready) continue;
      try {
        const options = JSON.parse(JSON.stringify(job.options));
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          options.autoplay = false;
          options.speed = 0;
          // Responsive options must not re-enable motion below a breakpoint.
          for (const breakpoint of options.responsive || []) {
            if (breakpoint.settings && typeof breakpoint.settings === 'object') {
              breakpoint.settings.autoplay = false;
              breakpoint.settings.speed = 0;
            }
          }
        }
        slick.call($(element), options);
        entry.instance = element.slick;
        entry.owned = element.classList.contains('slick-initialized');
        if (!entry.owned) throw new Error('carousel_not_initialized');
        element.dataset.fhCarouselState = 'ready';
      } catch (_) {
        cancelResponsiveTimer(element.slick);
        try { if (element.slick) slick.call($(element), 'unslick'); } catch (_) { /* restore below */ }
        cancelResponsiveTimer(element.slick);
        restore(entry);
        entry.owned = false;
        entry.instance = null;
        entry.failed = true;
        element.dataset.fhCarouselState = 'fallback';
      }
    }
  }

  function dispose(job) {
    job.disposed = true;
    if (job.media) job.media.removeEventListener('change', job.changed);
    teardown(job);
    jobs.delete(job.marker);
  }

  function register(marker) {
    if (jobs.has(marker)) return;
    let config;
    try { config = JSON.parse(marker.textContent); } catch (_) { return; }
    if (!config || typeof config.sectionId !== 'string' || typeof config.selector !== 'string' ||
        !config.options || typeof config.options !== 'object' || Array.isArray(config.options) ||
        (config.maxWidth !== undefined && (!Number.isFinite(config.maxWidth) || config.maxWidth <= 0))) return;
    const root = document.getElementById('shopify-section-' + config.sectionId);
    // Config must physically belong to the section it controls.
    if (!root || !root.contains(marker)) return;
    let elements;
    try { elements = Array.from(root.querySelectorAll(config.selector)); } catch (_) { return; }
    if (!elements.length || !elements.some(function (el) { return el.children.length; })) return;
    for (const previous of jobs.values()) {
      if (!previous.root.isConnected || (previous.root === root && previous.selector === config.selector)) dispose(previous);
    }
    const job = { marker, root, selector: config.selector, options: config.options, ready: false, disposed: false,
      entries: elements.map(function (element) {
        const children = Array.from(element.children);
        return { element, children, className: element.getAttribute('class'), style: element.getAttribute('style'),
          owned: false, instance: null, failed: false, childAttributes: children.map(function (child) {
            const values = {};
            for (const name of ['class', 'style', 'tabindex', 'aria-hidden', 'role', 'id']) values[name] = child.getAttribute(name);
            return { element: child, values };
          }) };
      }) };
    jobs.set(marker, job);
    if (typeof slick !== 'function') {
      for (const entry of job.entries) entry.element.dataset.fhCarouselState = 'fallback';
      return;
    }
    if (config.maxWidth) {
      job.media = window.matchMedia('(max-width: ' + config.maxWidth + 'px)');
      job.changed = function () { update(job); };
      job.media.addEventListener('change', job.changed);
    }
    styles().then(function (ready) {
      if (job.disposed) return;
      job.ready = ready;
      if (ready) update(job);
      else for (const entry of job.entries) entry.element.dataset.fhCarouselState = 'fallback';
    });
  }

  function scan(root) {
    // Also covers replacement HTML where the editor did not emit unload first.
    for (const job of jobs.values()) if (!job.root.isConnected || !job.marker.isConnected) dispose(job);
    if (root.matches && root.matches('script[data-fh-carousel]')) register(root);
    for (const marker of root.querySelectorAll('script[data-fh-carousel]')) register(marker);
  }

  window.FHSectionCarousels = Object.freeze({ scan });
  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    for (const job of jobs.values()) if (event.target === job.root || event.target.contains(job.root)) dispose(job);
  });
  scan(document);
})();
